"use client";

import { useRef } from "react";

import type { AuditionTrack, InspectTrack } from "@/lib/music/analyzer-types";
import type { AuditionState, MidiAuditionPlayer } from "@/lib/midi/midi-audition-player";
import { gmProgramLabel } from "@/lib/audio/gm-instruments";
import { CheckIcon, LoaderIcon, PauseIcon, PlayIcon } from "../../atoms/icons";

import { DensitySparkline } from "./density-sparkline";

interface TrackRowProps {
  /** Position inside the `AuditionPayload.tracks[]` array (0-based). */
  trackIdx: number;
  track: AuditionTrack;
  inspectTrack: InspectTrack | undefined;
  player: MidiAuditionPlayer | null;
  state: AuditionState;
  isSelected: boolean;
  isProbable: boolean;
  isDisabled: boolean;
  onToggleSelected: () => void;
  /** Fires once per solo-play click for telemetry. */
  onSoloPlay?: () => void;
}

const DEBOUNCE_MS = 200;

/** Title-cases the analyzer's GM instrument string ("electric guitar (clean)"). */
function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Best instrument label for a row. The analyzer ships a complete GM instrument
 * name (`inspectTrack.instrument`) for all 128 programs, so we prefer it over
 * the partial FR lookup, which would otherwise fall back to a useless
 * "Piste N" for anything outside its ~20 hand-picked entries.
 */
function resolveInstrumentLabel(
  track: AuditionTrack,
  inspectTrack: InspectTrack | undefined,
): string {
  if (track.isDrumChannel) {
    return "Batterie";
  }

  const raw = inspectTrack?.instrument?.trim();

  if (raw && raw.toLowerCase() !== "unknown") {
    return titleCase(raw);
  }

  return gmProgramLabel(track.gmProgram, undefined, false);
}

export function TrackRow({
  trackIdx,
  track,
  inspectTrack,
  player,
  state,
  isSelected,
  isProbable,
  isDisabled,
  onToggleSelected,
  onSoloPlay,
}: TrackRowProps) {
  const lastSoloClickAt = useRef(0);

  const isSoloPlaying =
    state.kind === "playing"
    && typeof state.mode === "object"
    && state.mode.trackIdx === trackIdx;

  const isLoadingPatch = state.kind === "loading";
  const isError = state.kind === "error";

  const handleSoloClick = () => {
    if (!player || isDisabled) {
      return;
    }

    const now = performance.now();

    if (now - lastSoloClickAt.current < DEBOUNCE_MS) {
      return;
    }

    lastSoloClickAt.current = now;

    if (isSoloPlaying) {
      player.stop();

      return;
    }

    player.playTrack(trackIdx);
    onSoloPlay?.();
  };

  const handleCheckboxClick = () => {
    if (isDisabled) {
      return;
    }

    onToggleSelected();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleCheckboxClick();
    }
  };

  const instrumentLabel = resolveInstrumentLabel(track, inspectTrack);
  const trackName = inspectTrack?.name?.trim() || `Piste ${trackIdx + 1}`;
  // 1-indexed MIDI channel to match what DAWs / notation tools display
  // (channels 1–16), so the user can map a row to "the channel 8 I see
  // elsewhere". The analyzer stores it 0-indexed.
  const channelLabel =
    inspectTrack?.channel != null ? `Canal ${inspectTrack.channel + 1}` : null;
  const subtitle = channelLabel
    ? `${channelLabel} · ${instrumentLabel}`
    : instrumentLabel;
  const noteCount = inspectTrack?.noteCount ?? track.notes.length;
  const disabledSuffix = isDisabled ? " · ignorée" : "";

  const rowBg = isSoloPlaying
    ? "rgba(139,92,246,0.18)"
    : isSelected
      ? "rgba(139,92,246,0.10)"
      : "transparent";

  return (
    <div
      role="group"
      aria-label={`${trackName} — ${instrumentLabel}`}
      className="flex items-center gap-3 transition-colors duration-150"
      style={{
        padding: "8px 14px",
        minHeight: 52,
        borderBottom: "1px solid var(--color-border-subtle)",
        backgroundColor: rowBg,
        boxShadow: isSelected ? "inset 2px 0 0 var(--color-accent)" : undefined,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? "not-allowed" : "default",
      }}
      onKeyDown={handleKeyDown}
    >
      {/* Checkbox */}
      <button
        type="button"
        role="checkbox"
        aria-checked={isSelected}
        aria-label={`Sélectionner ${trackName}`}
        disabled={isDisabled}
        onClick={handleCheckboxClick}
        className="flex shrink-0 items-center justify-center cursor-pointer"
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          border: isSelected
            ? "1.5px solid var(--color-accent)"
            : "1.5px solid var(--color-text-muted)",
          backgroundColor: isSelected ? "var(--color-accent)" : "transparent",
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {isSelected && <CheckIcon size={12} color="var(--color-bg-deep)" strokeWidth={3} />}
      </button>

      {/* Solo play button */}
      <button
        type="button"
        onClick={handleSoloClick}
        disabled={isDisabled || isLoadingPatch || !player}
        aria-label={
          isSoloPlaying ? "Mettre en pause cette piste" : `Écouter ${trackName}`
        }
        title={
          isDisabled
            ? "Cette piste ne peut pas être jouée au piano"
            : isSoloPlaying
              ? "Mettre en pause"
              : "Écouter cette piste"
        }
        className="flex shrink-0 items-center justify-center transition-colors duration-150 cursor-pointer"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: isSoloPlaying
            ? "1px solid var(--color-accent)"
            : "1px solid var(--color-border)",
          backgroundColor: isSoloPlaying ? "var(--color-accent)" : "transparent",
          color: isSoloPlaying ? "var(--color-bg-deep)" : "var(--color-text-secondary)",
          boxShadow: isSoloPlaying ? "0 0 0 2px rgba(139,92,246,0.25)" : "none",
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {isDisabled ? (
          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>—</span>
        ) : isLoadingPatch ? (
          <LoaderIcon size={12} className="animate-spin" />
        ) : isError ? (
          <span style={{ fontSize: 10, color: "var(--color-accent-warm)" }}>×</span>
        ) : isSoloPlaying ? (
          <PauseIcon size={12} />
        ) : (
          <PlayIcon size={12} />
        )}
      </button>

      {/* Track name + instrument (stacked, flexible) */}
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="flex items-center gap-2 min-w-0">
          <span
            className="text-[14px] font-medium truncate"
            style={{ color: "var(--color-text-primary)" }}
            title={trackName}
          >
            {trackName}
          </span>
          {inspectTrack?.isMelody && (
            <span
              title="Mélodie détectée"
              aria-label="Mélodie détectée"
              className="shrink-0 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide"
              style={{
                padding: "2px 6px",
                borderRadius: 999,
                color: "var(--color-accent)",
                backgroundColor: "rgba(139,92,246,0.12)",
                border: "1px solid rgba(139,92,246,0.30)",
              }}
            >
              🎤 Mélodie
            </span>
          )}
        </span>
        <span
          className="text-[12px] truncate"
          style={{ color: "var(--color-text-secondary)" }}
          title={subtitle}
        >
          {subtitle}
          {disabledSuffix}
        </span>
      </div>

      {/* Note count */}
      <span
        className="shrink-0 text-[12px] whitespace-nowrap"
        style={{
          color: "var(--color-text-muted)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {noteCount} notes
      </span>

      {/* Sparkline + star marker */}
      <div className="flex shrink-0 items-center gap-2">
        <DensitySparkline
          buckets={track.densityBuckets}
          width={80}
          height={20}
          variant="track"
          isActive={isSoloPlaying}
          isDisabled={isDisabled}
          isChecked={isSelected && !isSoloPlaying}
        />
        {isProbable && !isDisabled && (
          <span
            title="Probablement du piano"
            aria-label="Probablement du piano"
            style={{ color: "var(--color-accent)", fontSize: 12 }}
          >
            ★
          </span>
        )}
      </div>
    </div>
  );
}
